/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { ISchemaConstIntegerModel, TGenericModelNode } from "@adapptio/lib-blueprint";
import { FieldNumber } from "@adapptio/lib-editor-ui";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";

export const ControlConstInteger_Primary: TInspectorControlComponent<ISchemaConstIntegerModel> = ({
	model: _model,
	id
}) => {
	const { model, validationErrors } = useBlueprintModel(_model, "constInteger");

	const inspectorContext = useEditorInspectorContext();

	const handleChange = useCallback(
		(value: number) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldNumber
			id={id}
			value={model.value}
			onChange={handleChange}
			validationErrors={validationErrors}
			grow={true}
			step="1"
			readOnly={inspectorContext?.readOnly}
			debounceTimeout={inspectorContext?.inputDebounceTimeout}
			min={model.schema.opts.constraints?.min}
			max={model.schema.opts.constraints?.max}
		/>
	);
};

export const ControlConstInteger = (model: TGenericModelNode): IInspectorControls => ({
	primary: {
		component: ControlConstInteger_Primary,
		model: model
	}
});
